import { type FC, useEffect, useMemo, useRef } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Layout } from "@viagogo/core/ui";
import {
  Feature,
  RecordParticipationTypes,
  useFeatureParticipationRecorder,
  useFeatureTernary,
} from "@viagogo/feature";
import type { CategoryEventListViewModel } from "src/http/types";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { getLocationCookieOptions, useLocationContext } from "src/shared/context/location-context";
import { FilterTypes } from "src/shared/enums/enums";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation";
import type { Dates } from "src/types/shared/cookies";
import { CATEGORY_TYPE } from "src/website-apps/responsive/stubhub-category-grouping/components/constants";
import { useCategoryGroupingPageModelData } from "src/website-apps/responsive/stubhub-category-grouping/hooks/useCategoryGroupingPageModelData";
import { NewImmersiveHeaderWrapper } from "src/website-apps/shared/components/ancillary-pages/NewImmersiveHeaderWrapper";
import { branch } from "src/website-apps/shared/components/branch";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { ErrorMessage } from "src/website-apps/shared/components/ErrorMessage";
import { withDefaultContexts } from "src/website-apps/shared/components/explore/withDefaultContexts";
import { withExploreContexts } from "src/website-apps/shared/components/explore/withExploreContexts";
import { withExploreExp } from "src/website-apps/shared/components/explore/withExploreExp";
import { LazyFooter } from "src/website-apps/shared/components/footer";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { HeaderConfiguration } from "src/website-apps/shared/components/header";
import { EventsGridContextAwareHeader } from "src/website-apps/shared/components/header/EventsGridContextAwareHeader";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { MegaSearchContext, useMegaSearchContext } from "src/website-apps/shared/components/mega-search";
import { PrimaryRadiusInMeters } from "src/website-apps/shared/constants/grid-radius";
import { TOP_LEVEL_CATEGORY_IDS_LIST } from "src/website-apps/shared/constants/TopLevelCategory";
import { useDateCookieOptions } from "src/website-apps/shared/context/date-context";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useGridsReducer } from "src/website-apps/shared/hooks/useGridsReducer";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { getDateQueryParams } from "src/website-apps/shared/utils/getDateQueryParams";
import { getFilterQueryParams } from "src/website-apps/shared/utils/getFilterQueryParams";
import { getRecentlyViewedEventsFromCookies } from "src/website-apps/shared/utils/recently-viewed";
import { selectReducerGrids } from "src/website-apps/shared/utils/selectReducerGrids";
import { CategoryTopPerformersCarousel } from "./CategoryTopPerformersCarousel";
import { LayoutBodyManager } from "./LayoutBodyManager";
import {
  featuredCategoryIdsForVariant,
  featuredShowModuleForVariant,
  THEATER_PROMO_BANNER_CATEGORY_IDS,
  TheaterPromoBannerFeature,
} from "./TheaterPromoBannerFeature";

const resolveInitialFavoritesState = (model: CategoryEventListViewModel) => {
  const events = [...(model.trendingEvents?.items ?? [])];
  const eventFavorites = Object.fromEntries(events.map((curr) => [curr.eventId ?? -1, curr.isFavorite]));
  return { [ENTITY_TYPES.EVENT]: { ...eventFavorites } };
};

interface Props {
  model: CategoryEventListViewModel;
  dates?: Maybe<Dates>;
}

const AppControlComponent: FC<Props> = ({ dates, model }) => {
  const appContext = useAppContext();
  const { env } = appContext;
  const megaSearchContext = useMegaSearchContext();
  const cookieOptions = useMemo(() => getLocationCookieOptions(appContext), [appContext]);
  const [initialLocation] = useLocationContext(cookieOptions);
  const pageModel = useCategoryGroupingPageModelData();
  const initialFavorites = useMemo(() => resolveInitialFavoritesState(pageModel), [pageModel]);
  const dateCookieOptions = useDateCookieOptions();
  const execDateWithExpiration = useFeatureTernary(FeatureSetEnum.EventListExpireDateFilter);
  const dateWithExpiration = useMemo(
    () =>
      execDateWithExpiration({
        control: () => false,
        variant: () => true,
      }) ?? undefined,
    [execDateWithExpiration],
  );
  const categoryTypeId = model.categorySummary?.categoryTypeId;
  const execDefaultGridFilterType = useFeatureTernary(
    FeatureSetEnum.TMGroupingParkingPagesShowParkingEventsByDefault,
    () => categoryTypeId === CATEGORY_TYPE.PARKING,
  );
  const defaultGridFilterType = useMemo(
    () =>
      execDefaultGridFilterType({
        control: () => FilterTypes.EVENTS,
        variant: () => (categoryTypeId === CATEGORY_TYPE.PARKING ? FilterTypes.PARKING : FilterTypes.EVENTS),
        record: false,
      }),
    [categoryTypeId, execDefaultGridFilterType],
  );
  const { search } = useWindowLocation();
  const now = useStableNow();
  const datesQueryParams = useMemo(
    () => getDateQueryParams(env, search, now, dateWithExpiration, dateCookieOptions),
    [env, search, now, dateWithExpiration, dateCookieOptions],
  );
  const filters = useMemo(
    () =>
      getFilterQueryParams(search, {
        gridFilterType: defaultGridFilterType,
      }),
    [search, defaultGridFilterType],
  );

  const [mobileMenuOpen, handleOnMobileMenuToggle] = useScrollToggle();

  const participationRecorder = useFeatureParticipationRecorder(FeatureSetEnum.TMGlobalTrendingOnAncillaryPages);
  useEffect(() => {
    participationRecorder();
  }, [participationRecorder]);

  const immersiveHeaderOnAncillary = useFeatureTernary(FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages);

  const showImmersiveHeader = useMemo(
    () =>
      immersiveHeaderOnAncillary({
        control: () => false,
        variant: () => true,
        record: false,
      }),
    [immersiveHeaderOnAncillary],
  );

  const promoBannerOnPage = THEATER_PROMO_BANNER_CATEGORY_IDS.includes(model.categoryId);
  // Server-computed: false for Pricing's learning-arm sessions, which must not enter this experiment.
  const promoBannerEligible = model.isPromoBannerEligible === true;
  const execPromoBannerVariant = useFeatureTernary(FeatureSetEnum.TPromoBannerATG2b);
  const promoBannerVariant = useMemo(
    () =>
      promoBannerOnPage && promoBannerEligible
        ? (execPromoBannerVariant({ control: () => 0, variant: (_data, variant) => variant, record: false }) ?? 0)
        : 0,
    [execPromoBannerVariant, promoBannerOnPage, promoBannerEligible],
  );
  const promoBannerActive = promoBannerVariant > 0;
  // On phone the promo hero is immersive, so the nav goes into its transparent (immersive) variant
  // and overlays the top of the image. From tablet up the nav stays on the light chrome as a strip.
  const isPhone = !useMediaQuery(Breakpoint.Tablet);
  const promoFeaturedCategoryIds = featuredCategoryIdsForVariant(promoBannerVariant);
  const promoShowKicker = useContent("cid$#4434606#"); // "15 outrageous years on Broadway"
  const promoFeaturedShow = featuredShowModuleForVariant(promoBannerVariant, promoShowKicker);

  const recentlyViewedEventsArray = useMemo(() => getRecentlyViewedEventsFromCookies(env), [env]);

  const reducerOptions = useMemo(
    () => ({
      ...selectReducerGrids(model.eventGrids),
      primaryFromRadius: 0,
      primaryToRadius: PrimaryRadiusInMeters,
      restFromRadius: PrimaryRadiusInMeters,
      restToRadius: null,
      method: "GetFilteredEvents",
      categoryId: model.categoryId,
      location: initialLocation,
      dates: datesQueryParams,
      sortBy: filters.sortBy,
      gridFilterType: filters.gridFilterType,
      eventViewType: filters.eventViewType,
      calendarEvents: model.calendarEvents,
      recentlyViewedEvents: recentlyViewedEventsArray,
    }),
    [
      datesQueryParams,
      filters.eventViewType,
      filters.gridFilterType,
      filters.sortBy,
      initialLocation,
      model.calendarEvents,
      model.categoryId,
      model.eventGrids,
      recentlyViewedEventsArray,
    ],
  );
  const gridsContext = useGridsReducer(reducerOptions);
  const [gridsState] = gridsContext;

  // SSR ships empty grids — always fetch from LocationSearch on mount.
  const initialGridsContext = useRef(gridsContext);
  useEffect(() => {
    const [state, dispatch, methods] = initialGridsContext.current;
    methods.setDates(dispatch, state, state.filters.dates ?? undefined);
  }, []);

  return (
    <div className={pageWrapperRecipe({ mobileMenu: mobileMenuOpen ? "open" : "closed" })}>
      <EventsGridContext.Provider value={gridsContext}>
        <FavoritesProvider data={initialFavorites}>
          <Layout
            header={
              <MegaSearchProvider>
                <NewImmersiveHeaderWrapper
                  tlcId={
                    !model.isImmersivePage &&
                    model.categoryId &&
                    !TOP_LEVEL_CATEGORY_IDS_LIST.includes(model.categoryId)
                      ? model.tlcId
                      : null
                  }
                  headerTitle={model.categorySummary?.categoryName}
                  categoryId={model.categoryId}
                  dates={dates}
                  location={gridsState.filters.location}
                  leagueContent={model.leagueHeaderContent}
                  logoUrl={model.leagueLogoImageUrl}
                  hideTrending
                  // Control only: main renders Top Performers inside the immersive header (CXWS-2831).
                  // The promo variants keep it in the body card instead, so it is not passed here.
                  carouselSlot={
                    !promoBannerActive && !model.isImmersivePage && model.popularCategories ? (
                      <CategoryTopPerformersCarousel
                        categoryId={model.categoryId}
                        categoryName={model.categorySummary?.categoryName ?? undefined}
                        popularCategories={model.popularCategories}
                      />
                    ) : undefined
                  }
                  promoActive={promoBannerActive}
                  promoSlot={
                    promoBannerOnPage && promoBannerEligible ? <TheaterPromoBannerFeature model={model} /> : null
                  }
                  promoNav={
                    <Feature
                      id={FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages}
                      recordType={RecordParticipationTypes.MANUAL}
                      control={() => (
                        <MegaSearchContext.Provider value={megaSearchContext}>
                          <EventsGridContextAwareHeader
                            browseHeader
                            discoveryLinks={model.header.navigationBarLinks}
                            isUserLoggedIn={appContext.isUserLoggedIn}
                            onMobileMenuToggle={handleOnMobileMenuToggle}
                            mobileMenuOpen={mobileMenuOpen}
                          />
                        </MegaSearchContext.Provider>
                      )}
                      variant={() => (
                        <MegaSearchContext.Provider value={megaSearchContext}>
                          <HeaderConfiguration.Browse
                            discoveryLinks={model.header.navigationBarLinks}
                            isUserLoggedIn={appContext.isUserLoggedIn}
                            onMobileMenuToggle={handleOnMobileMenuToggle}
                            bigPerformerBanner={
                              (!!showImmersiveHeader && !promoBannerActive) || (promoBannerActive && isPhone)
                            }
                            mobileMenuOpen={mobileMenuOpen}
                          />
                        </MegaSearchContext.Provider>
                      )}
                    />
                  }
                >
                  <ErrorMessage />
                  {!appContext.isNativeApp && !megaSearchContext.active && (
                    <MarketplaceDisclosure marketplaceDisclosureSiteMessage={model.header.marketplaceDisclosure} />
                  )}
                </NewImmersiveHeaderWrapper>
              </MegaSearchProvider>
            }
            body={
              <LayoutBodyManager
                model={model}
                featuredCategoryIds={promoFeaturedCategoryIds}
                featuredShow={promoFeaturedShow}
                overlapsHero={promoBannerActive}
              />
            }
            footer={
              <LazyFooter
                companyLinks={model.footer.companyLinks}
                supportLinks={model.footer.supportLinks}
                getLocationSettingsUrl={model.footer.getLocationSettingsUrl}
              />
            }
          />
        </FavoritesProvider>
      </EventsGridContext.Provider>
    </div>
  );
};

export const AppControlComponentWithContexts = branch(
  (props: Props & { exploreEnabled?: Maybe<boolean> }) => !!props.exploreEnabled,
  withExploreContexts,
  withDefaultContexts,
)(AppControlComponent);
export const AppControl = withExploreExp(AppControlComponentWithContexts);
